// Тарифы: единственный источник правды по лимитам.
//
// Здесь только цифры и признаки — ни разметки, ни текстов. Так этот же файл
// потом читает сервер, когда начнёт эти лимиты проверять: страница с прайсингом
// и настоящий отказ «больше нельзя» обязаны говорить одно и то же, а два
// независимых списка лимитов расходятся на первой же правке.
//
// Тексты живут в словарях (`pricing.*`), сюда попадают только ключи.
import type { TranslationKey } from '../i18n';

export const PLANS = ['free', 'pro'] as const;
export type PlanId = (typeof PLANS)[number];

/**
 * Цена в евро. Годовая — не 12×месячная: у планирования поездок сезонный
 * характер (две недели в мае, потом тишина до ноября), и помесячная подписка
 * на таком ритме отваливается сразу после поездки. Скидка за год — плата за то,
 * чтобы человек не отписывался в июне.
 */
export const PRICE = {
  currency: '€',
  monthly: 6,
  yearly: 48,
} as const;

/** Скидка годового плана в процентах — считается, а не пишется руками. */
export const YEARLY_DISCOUNT = Math.round((1 - PRICE.yearly / (PRICE.monthly * 12)) * 100);

/**
 * Значение лимита в ячейке таблицы.
 *
 * `count` разделён на `total` и `month`, потому что это разные обещания:
 * 10 поездок — это потолок, который занимаешь один раз, а 100 распознаваний —
 * то, что возобновляется. Одна и та же цифра без этой пометки читается неверно.
 */
export type Limit =
  | { kind: 'count'; value: number; per: 'total' | 'month' }
  | { kind: 'unlimited' }
  | { kind: 'yes' }
  | { kind: 'none' }
  | { kind: 'text'; key: TranslationKey };

export interface PlanFeature {
  id: string;
  /** Раздел таблицы. */
  section: SectionId;
  titleKey: TranslationKey;
  free: Limit;
  pro: Limit;
}

export const SECTIONS = ['planner', 'collab', 'ai', 'advisor', 'export', 'watch'] as const;
export type SectionId = (typeof SECTIONS)[number];

const n = (value: number, per: 'total' | 'month' = 'month'): Limit => ({
  kind: 'count',
  value,
  per,
});
const unlim: Limit = { kind: 'unlimited' };
const yes: Limit = { kind: 'yes' };
const none: Limit = { kind: 'none' };

export const FEATURES: PlanFeature[] = [
  // ── Планировщик ────────────────────────────────────────────────────────
  { id: 'trips', section: 'planner', titleKey: 'pricing.f.trips', free: n(1, 'total'), pro: n(10, 'total') },
  { id: 'variants', section: 'planner', titleKey: 'pricing.f.variants', free: n(2, 'total'), pro: unlim },
  { id: 'library', section: 'planner', titleKey: 'pricing.f.library', free: n(20, 'total'), pro: unlim },
  { id: 'snapshots', section: 'planner', titleKey: 'pricing.f.snapshots', free: n(2, 'total'), pro: n(50, 'total') },
  { id: 'currencies', section: 'planner', titleKey: 'pricing.f.currencies', free: n(3, 'total'), pro: unlim },

  // ── Совместная работа ──────────────────────────────────────────────────
  { id: 'coauthors', section: 'collab', titleKey: 'pricing.f.coauthors', free: n(1, 'total'), pro: unlim },
  {
    id: 'shareLife',
    section: 'collab',
    titleKey: 'pricing.f.shareLife',
    free: { kind: 'text', key: 'pricing.value.month1' },
    pro: { kind: 'text', key: 'pricing.value.forever' },
  },
  { id: 'voting', section: 'collab', titleKey: 'pricing.f.voting', free: none, pro: yes },

  // ── ИИ ─────────────────────────────────────────────────────────────────
  { id: 'recognize', section: 'ai', titleKey: 'pricing.f.recognize', free: n(1), pro: unlim },
  { id: 'agent', section: 'ai', titleKey: 'pricing.f.agent', free: n(2), pro: unlim },
  { id: 'alternatives', section: 'ai', titleKey: 'pricing.f.alternatives', free: none, pro: unlim },
  { id: 'connections', section: 'ai', titleKey: 'pricing.f.connections', free: n(10), pro: unlim },
  { id: 'mailIn', section: 'ai', titleKey: 'pricing.f.mailIn', free: n(2), pro: n(100) },
  { id: 'summary', section: 'ai', titleKey: 'pricing.f.summary', free: n(1), pro: n(100) },

  // ── Подсказчик ─────────────────────────────────────────────────────────
  { id: 'dossier', section: 'advisor', titleKey: 'pricing.f.dossier', free: none, pro: unlim },
  { id: 'visa', section: 'advisor', titleKey: 'pricing.f.visa', free: n(10), pro: unlim },
  { id: 'weather', section: 'advisor', titleKey: 'pricing.f.weather', free: n(1), pro: unlim },
  { id: 'local', section: 'advisor', titleKey: 'pricing.f.local', free: n(1), pro: unlim },
  { id: 'tips', section: 'advisor', titleKey: 'pricing.f.tips', free: n(1), pro: unlim },
  { id: 'living', section: 'advisor', titleKey: 'pricing.f.living', free: n(1), pro: unlim },

  // ── Экспорт ────────────────────────────────────────────────────────────
  { id: 'pdf', section: 'export', titleKey: 'pricing.f.pdf', free: n(2), pro: unlim },
  { id: 'ics', section: 'export', titleKey: 'pricing.f.ics', free: none, pro: yes },
  { id: 'csv', section: 'export', titleKey: 'pricing.f.csv', free: none, pro: yes },
  { id: 'sheets', section: 'export', titleKey: 'pricing.f.sheets', free: none, pro: yes },
  { id: 'offline', section: 'export', titleKey: 'pricing.f.offline', free: none, pro: yes },

  // ── Слежение ───────────────────────────────────────────────────────────
  { id: 'priceWatch', section: 'watch', titleKey: 'pricing.f.priceWatch', free: none, pro: n(10, 'total') },
  { id: 'flightAlerts', section: 'watch', titleKey: 'pricing.f.flightAlerts', free: none, pro: n(10, 'total') },
  { id: 'reminders', section: 'watch', titleKey: 'pricing.f.reminders', free: n(2, 'total'), pro: n(10, 'total') },
  { id: 'deadlines', section: 'watch', titleKey: 'pricing.f.deadlines', free: none, pro: n(100, 'total') },
];

/**
 * Строки, по которым выбирают тариф. Их видно сразу, остальные лежат под
 * раскрывающимся списком.
 *
 * Десять — не круглое число ради красоты: столько помещается на экран телефона
 * без прокрутки, а дальше сравнение перестаёт быть сравнением и становится
 * чтением. Тридцать строк подряд не убеждают никого — их пролистывают.
 *
 * Порядок здесь свой, а не порядок разделов: наверху то, во что упираются
 * первым, а не то, что первым лежит в каталоге. Сами разделы в этом списке не
 * показываются вовсе — шесть заголовков на десять строк съели бы весь выигрыш.
 * Но представлен каждый: раздел, не попавший в главное, для читателя карточек
 * не существует, а платят в том числе за него.
 *
 * Набор живёт в коде, а не в документе админки, по той же причине, что и
 * словарь значений: это устройство таблицы, а не её содержимое. Строку можно
 * выключить или переименовать из админки — и она пропадёт отсюда сама, потому
 * что выключенные строки сюда не доходят.
 */
export const KEY_FEATURES: readonly string[] = [
  'trips',
  'variants',
  'recognize',
  'agent',
  'alternatives',
  'coauthors',
  'shareLife',
  'dossier',
  'pdf',
  'priceWatch',
];

/** Что видно в карточке тарифа: пять строк, а не вся таблица. */
export const HIGHLIGHTS: Record<PlanId, TranslationKey[]> = {
  free: [
    'pricing.f.trips',
    'pricing.f.variants',
    'pricing.f.recognize',
    'pricing.f.coauthors',
    'pricing.f.pdf',
  ],
  pro: [
    'pricing.f.trips',
    'pricing.f.recognize',
    'pricing.f.agent',
    'pricing.f.dossier',
    'pricing.f.priceWatch',
  ],
};

/**
 * Задуманное, но не сделанное. Отдельным блоком, а не строками таблицы с
 * пометкой «скоро»: обещание в колонке тарифа читается как то, за что уже
 * заплачено, и на этом строится половина претензий к платным продуктам.
 */
export const SOON: TranslationKey[] = [
  'pricing.soon.optimizer',
  'pricing.soon.split',
  'pricing.soon.routes',
  'pricing.soon.roles',
  'pricing.soon.leaderboard',
  'pricing.soon.affiliate',
  'pricing.soon.api',
  'pricing.soon.whitelabel',
];

/** Разовые покупки: для тех, кому подписка не подходит по сценарию. */
export const EXTRAS: { id: string; titleKey: TranslationKey; hintKey: TranslationKey; price: number }[] = [
  // `dossier-once`, а не `dossier`: строка таблицы с таким id уже есть, и два
  // одинаковых имени в одном документе путают того, кто правит его в админке.
  { id: 'dossier-once', titleKey: 'pricing.extras.dossier', hintKey: 'pricing.extras.dossier.hint', price: 2 },
  { id: 'pack', titleKey: 'pricing.extras.pack', hintKey: 'pricing.extras.pack.hint', price: 5 },
];

export function featuresOf(section: SectionId): PlanFeature[] {
  return FEATURES.filter((f) => f.section === section);
}

export function findFeature(titleKey: TranslationKey): PlanFeature | undefined {
  return FEATURES.find((f) => f.titleKey === titleKey);
}
